import { Button, Icon, IconSize } from "@blueprintjs/core";
import classNames from "classnames";
import { ScaleLinear, scaleLinear } from "d3";
import React, { MouseEvent, useMemo, useRef } from "react";

import { useElementResponsiveDimensions } from "../../hooks";
import { stepBackwardIcon } from "../../images/custom-icons";
import { multiFrameIcon } from "../../images/custom-icons/multi-frame";
import { singleFrameIcon } from "../../images/custom-icons/single-frame";
import { usePlayContext } from "../../pages/Play/play-context";
import { MarkerPlayer } from "../../types";
import styles from "./animation-timeline.module.scss";
import { ActionBar, PlayerAxis, Playhead, TimelineAxis } from "./components";
import { TIMELINE_VIEW, useGetPlayerScale, useGetPlayersWithAnimation } from "./hooks";

const PLAYER_AXIS_WIDTH = 60;
const TIMELINE_AXIS_HEIGHT = 30;

interface AnimationTimelineProps {
  height: number;
}

export const AnimationTimeline = ({ height }: AnimationTimelineProps) => {
  const { playStateApi, animationTimerApi } = usePlayContext();
  const { activeFrame } = playStateApi;
  const {
    time,
    setTime,
    timelineView,
    setTimelineView,
    frameToStartTimeScale,
    isPlaying,
    playTimer,
    pauseTimer,
    resetTimer,
    maxTime,
    setMaxTime,
  } = animationTimerApi;

  // We use an svg container ref because the useElementResponsiveDimensions does not work properly with svg elements
  const svgContainerRef = useRef<HTMLDivElement>(null);

  // get the width of the svg container to responsively update the child components as window is resized
  const { width: svgWidth, height: svgHeight } = useElementResponsiveDimensions(svgContainerRef);

  const timelineScale = useMemo(
    (): ScaleLinear<number, number> =>
      scaleLinear()
        .domain([0, maxTime])
        .range([0, svgWidth - PLAYER_AXIS_WIDTH]),
    [maxTime, svgWidth]
  );

  const framesPlayersWithAnimation = useGetPlayersWithAnimation();
  const distinctPlayers = useMemo(
    () =>
      framesPlayersWithAnimation
        .flatMap((frame) => frame.playersWithAnimation)
        .reduce<MarkerPlayer[]>(
          (acc, player) => (acc.map((p) => p.id).includes(player.id) ? acc : [...acc, player]),
          []
        ),
    [framesPlayersWithAnimation]
  );

  const playerScale = useGetPlayerScale({ players: distinctPlayers, height: svgHeight - TIMELINE_AXIS_HEIGHT - 40 });

  const handleAddTime = () => {
    setMaxTime((prevState) => prevState + 500);
  };

  return (
    <div ref={svgContainerRef} className={classNames("bp4-dark", styles.animationTimeline)} style={{ height }}>
      <svg className={styles.animationTimelineSvg}>
        <TimelineAxis xOffset={PLAYER_AXIS_WIDTH} scale={timelineScale} setTime={setTime} />
        <PlayerAxis
          xOffset={PLAYER_AXIS_WIDTH}
          yOffset={TIMELINE_AXIS_HEIGHT}
          scale={playerScale}
          players={distinctPlayers}
        />

        <g transform={`translate(${PLAYER_AXIS_WIDTH}, ${TIMELINE_AXIS_HEIGHT})`}>
          {framesPlayersWithAnimation.map((frame, frame_i, frames) => (
            <g
              transform={`translate(${
                timelineView === TIMELINE_VIEW.ALL ? timelineScale(frameToStartTimeScale(frame.frameId)) : 0
              }, 0)`}
              className={classNames(styles.animationFrame, {
                [styles.active || ""]: frame.frameId === activeFrame?.id,
              })}
              onClick={() => {
                if (frame.frameId !== activeFrame?.id) {
                  setTime(frameToStartTimeScale(frame.frameId));
                }
              }}
              key={frame_i}
            >
              {frame.playersWithAnimation.map((player, player_i) => (
                <g transform={`translate(0, ${playerScale(player.id)})`} key={player_i}>
                  {player.actions.map((action, action_i) => (
                    <ActionBar
                      action={action}
                      playerScale={playerScale}
                      timelineScale={timelineScale}
                      canEdit={activeFrame?.id === frame.frameId}
                      key={action_i}
                    />
                  ))}
                </g>
              ))}
              {!(activeFrame?.id === frame.frameId) && (
                <rect
                  x={0}
                  y={0}
                  width={Math.max(
                    timelineScale(frameToStartTimeScale(frames[frame_i + 1]?.frameId || "") || 0) -
                      timelineScale(frameToStartTimeScale(frame.frameId) || 0),
                    0
                  )}
                  height={svgHeight}
                  fill="transparent"
                  className={styles.frameCover}
                />
              )}
            </g>
          ))}
        </g>

        <Playhead
          x={PLAYER_AXIS_WIDTH + timelineScale(time)}
          xOffset={PLAYER_AXIS_WIDTH}
          parentHeight={svgHeight}
          scale={timelineScale}
          setTime={setTime}
        />
      </svg>
      <div className={styles.timelineControls}>
        <div>
          <Button
            active={timelineView === TIMELINE_VIEW.FRAME}
            outlined={timelineView === TIMELINE_VIEW.FRAME}
            icon={<Icon icon={singleFrameIcon} />}
            onClick={(e: MouseEvent<HTMLElement>) => {
              e.currentTarget.blur();
              setTime(0);
              setTimelineView(TIMELINE_VIEW.FRAME);
            }}
          />
          <Button
            className={styles.mgLeft}
            icon={<Icon icon={multiFrameIcon} />}
            active={timelineView === TIMELINE_VIEW.ALL}
            outlined={timelineView === TIMELINE_VIEW.ALL}
            onClick={(e: MouseEvent<HTMLElement>) => {
              e.currentTarget.blur();
              setTime(0);
              setTimelineView(TIMELINE_VIEW.ALL);
            }}
          />
        </div>
        <div>
          <Button icon={<Icon icon={stepBackwardIcon} size={IconSize.LARGE} />} minimal small onClick={resetTimer} />
          {isPlaying ? (
            <Button icon={<Icon icon="pause" size={IconSize.LARGE} />} minimal small onClick={pauseTimer} />
          ) : (
            <Button
              icon={<Icon icon="play" className={styles.playIcon} size={IconSize.LARGE} />}
              minimal
              small
              onClick={playTimer}
            />
          )}
        </div>
      </div>
      <div className={styles.addTime} onClick={handleAddTime}>
        <Icon icon={"add"} />
      </div>
    </div>
  );
};
